import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import platformClient, { platformAPI } from '../services/platformApi';

/**
 * The operator's session, kept apart from the tenant auth store.
 *
 * A separate store under a separate storage key, so that an operator who is
 * also a tenant admin (the usual case for the person who runs this) can hold
 * both sessions in one browser without one logging the other out. The token
 * is a platform-audience JWT that no tenant route accepts, and vice versa.
 *
 * No refresh token: the backend issues short-lived tokens and the console
 * simply asks for a new login when one expires.
 */
export const usePlatformAuthStore = create(
  persist(
    (set, get) => ({
      token: null,
      operator: null,
      isAuthenticated: false,
      isInitializing: true,
      error: null,

      initialize: async () => {
        const { token } = get();
        if (!token) {
          set({ isInitializing: false, isAuthenticated: false, operator: null });
          return;
        }
        platformClient.setToken(token);
        try {
          const response = await platformAPI.me();
          set({ operator: response.data.operator, isAuthenticated: true, isInitializing: false, error: null });
        } catch {
          platformClient.setToken(null);
          set({ token: null, operator: null, isAuthenticated: false, isInitializing: false });
        }
      },

      login: async (email, password) => {
        set({ error: null });
        try {
          const response = await platformAPI.login(email, password);
          const { accessToken, operator } = response.data;
          platformClient.setToken(accessToken);
          set({ token: accessToken, operator, isAuthenticated: true, isInitializing: false, error: null });
          return { success: true };
        } catch (error) {
          let message = 'Login failed. Please try again.';
          if (error.response?.status === 429) message = error.response.data?.error || 'Too many attempts. Please wait a few minutes.';
          else if (error.response?.data?.error) message = error.response.data.error;
          else if (!error.response) message = 'Unable to reach the server.';
          set({ error: message });
          return { success: false, error: message };
        }
      },

      logout: async () => {
        try {
          await platformAPI.logout();
        } catch {
          // The token is being discarded either way.
        }
        platformClient.setToken(null);
        set({ token: null, operator: null, isAuthenticated: false, isInitializing: false, error: null });
      },

      /** A 401 after login means the token died; drop it rather than retry. */
      expire: () => {
        platformClient.setToken(null);
        set({ token: null, operator: null, isAuthenticated: false, isInitializing: false });
      },
    }),
    {
      name: 'halomts-platform-auth',
      // sessionStorage, not localStorage: the operator token provisions and
      // suspends every tenant, and a page with tenant-authored HTML must not be
      // able to read it after the tab closes.
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) => ({ token: state.token }),
      onRehydrateStorage: () => () => {
        setTimeout(() => usePlatformAuthStore.getState().initialize(), 0);
      },
    }
  )
);

platformClient.onUnauthorized(() => usePlatformAuthStore.getState().expire());

export default usePlatformAuthStore;
